// src/pages/Faq.jsx
import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import "../global.css";
import "../styles/faqPage.css";

const Faq = () => {
  return (
    <div className="faq-page">
      <Helmet>
        <title>
          Heathrow Taxi, Airport & Minibus FAQ | CheapCabGrab
        </title>
        <meta
          name="description"
          content="Answers to common Heathrow taxi and airport questions: immigration times, terminals, flight status, parking vs taxi, train vs taxi, minibus & coach hire, drivers and payments."
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/faq"
        />
        <meta
          name="keywords"
          content="Heathrow FAQ, Heathrow taxi questions, Heathrow immigration time, Heathrow terminal finder, Heathrow parking vs taxi, Heathrow train vs taxi, airport transfer help, minibus hire FAQ"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Heathrow Taxi, Airport & Minibus FAQ | CheapCabGrab"
        />
        <meta
          property="og:description"
          content="Quick answers to Heathrow airport transfer, immigration timing, terminal finder, parking vs taxi, train vs taxi, minibus and coach hire questions."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/faq"
        />

        {/* FAQPage JSON-LD – Heathrow-focused Q&A */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How can I check my Heathrow flight status?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "You can use the CheapCabGrab Heathrow Flight Checker tool to see live arrival and departure information by flight number, including terminal, scheduled time and status. Visit https://www.cheapcabgrab.com/flight-checker to check your flight before you travel."
                }
              },
              {
                "@type": "Question",
                name: "How long does it usually take to get through Heathrow immigration?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Immigration times at Heathrow vary by terminal, time of day, passport type and how busy flights are. Our Heathrow Arrival Assistant uses recent queue snapshots and your flight details to give a simple estimate of immigration and baggage time, plus a step-by-step arrival guide. You can try it at https://www.cheapcabgrab.com/arrival-assistant."
                }
              },
              {
                "@type": "Question",
                name: "What is the best way to get from Heathrow to London – taxi or train?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "The best option depends on your budget, luggage, travel time and how many people are travelling. Our Taxi vs Train tool compares journey time, changes, walking, hassle and typical costs between Heathrow and London. You can also see fixed-price Heathrow taxi fares on our Heathrow Taxi page. Visit https://www.cheapcabgrab.com/train-vs-taxi and https://www.cheapcabgrab.com/heathrow-taxi for more detail."
                }
              },
              {
                "@type": "Question",
                name: "Should I park at Heathrow or book a taxi instead?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "If you are driving to the airport, you will need to consider fuel, parking charges and time. Our Parking vs Taxi calculator compares typical Heathrow parking costs against a fixed-price taxi so you can decide what is best for your trip. Try it at https://www.cheapcabgrab.com/heathrow-parking-vs-taxi."
                }
              },
              {
                "@type": "Question",
                name: "How do I find my terminal and airline at Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "If you are unsure which terminal your flight uses, you can use our Terminal Finder tool to look up Heathrow terminals by airline or route. We also provide quick links to airline contact numbers. See https://www.cheapcabgrab.com/terminalFinder and https://www.cheapcabgrab.com/airlineContacts."
                }
              },
              {
                "@type": "Question",
                name: "Do you track Heathrow flights and delays for airport pickups?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. For airport pickups we monitor flight status wherever possible and adjust driver arrival times when there are delays. You can also check live flight updates using our Heathrow Flight Checker and view wider disruption on our Travel News page."
                }
              },
              {
                "@type": "Question",
                name: "How do I book a taxi or minibus with CheapCabGrab?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "You can book online using the Instant Fare Quote form on our homepage. Enter your pickup and drop-off locations, passengers and luggage to see a fixed-price fare. For 5 or more passengers, you can also request minibus or coach hire using our dedicated minibus and coach pages."
                }
              },
              {
                "@type": "Question",
                name: "Are your drivers licensed and DBS checked?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "All CheapCabGrab drivers are licensed by their local council, insured for hire and reward and follow strict UK private hire rules. Enhanced DBS checks, right-to-work checks and medical standards are part of the licensing process. Many drivers also complete safeguarding and disability awareness training."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="faq-grid">
        {/* LEFT COLUMN – MAIN FAQ CONTENT */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>❓ Frequently Asked Questions</h1>
            <p>
              Helpful answers to Heathrow taxi, airport transfer, minibus and
              coach questions. If you still need help,{" "}
              <Link className="faq-link" to="/contact">
                contact our support team
              </Link>
              .
            </p>
          </header>

          {/* HEATHROW & AIRPORT FOCUSED FAQ */}
          <section className="faq-section" id="heathrow">
            <h2>✈️ Heathrow & Airport FAQs</h2>

            <div className="faq-item">
              <h3>How can I check my Heathrow flight status?</h3>
              <p>
                Use our{" "}
                <Link className="faq-link" to="/flight-checker">
                  Heathrow Flight Checker
                </Link>{" "}
                tool. Enter your flight number to see{" "}
                <strong>live arrivals and departures</strong>, including
                terminal, scheduled time and current status.
              </p>
            </div>

            <div className="faq-item">
              <h3>How long will immigration and baggage take at Heathrow?</h3>
              <p>
                Immigration times vary by terminal, time of day and passport
                type. Our{" "}
                <Link className="faq-link" to="/arrival-assistant">
                  Heathrow Arrival Assistant
                </Link>{" "}
                uses recent queue snapshots and your flight details to give a{" "}
                <strong>simple estimate</strong> of immigration and baggage
                timing, plus a step-by-step guide from the plane door to the
                arrivals hall.
              </p>
            </div>

            <div className="faq-item">
              <h3>Which Heathrow terminal does my airline use?</h3>
              <p>
                You can use our{" "}
                <Link className="faq-link" to="/terminalFinder">
                  Terminal Finder
                </Link>{" "}
                to look up your airline and see which terminal it normally uses
                (T2, T3, T4 or T5). For contact numbers, see{" "}
                <Link className="faq-link" to="/airlineContacts">
                  Airline Contacts
                </Link>
                .
              </p>
            </div>

            <div className="faq-item">
              <h3>
                What is the best way to get from Heathrow to London – taxi or
                train?
              </h3>
              <p>
                It depends on your budget, luggage and how many people are
                travelling. Our{" "}
                <Link className="faq-link" to="/train-vs-taxi">
                  Taxi vs Train
                </Link>{" "}
                tool compares{" "}
                <strong>journey time, changes, walking and hassle</strong> for
                Heathrow routes. You can also see fixed-price fares on our{" "}
                <Link className="faq-link" to="/heathrow-taxi">
                  Heathrow Taxi
                </Link>{" "}
                page and dedicated route pages such as{" "}
                <Link className="faq-link" to="/heathrow-to-london">
                  Heathrow to London
                </Link>
                .
              </p>
            </div>

            <div className="faq-item">
              <h3>Should I drive and park at Heathrow or book a taxi?</h3>
              <p>
                If you have a car, you will need to factor in{" "}
                <strong>fuel, parking charges and time</strong>. Our{" "}
                <Link className="faq-link" to="/heathrow-parking-vs-taxi">
                  Parking vs Taxi
                </Link>{" "}
                calculator compares typical Heathrow parking prices against a{" "}
                fixed-price taxi so you can decide which is best for your trip.
              </p>
            </div>

            <div className="faq-item">
              <h3>Do you track flights and delays for Heathrow pickups?</h3>
              <p>
                Yes, we monitor flight status wherever possible and adjust
                driver arrival times when there are delays. You can also check
                your flight with our{" "}
                <Link className="faq-link" to="/flight-checker">
                  Flight Checker
                </Link>{" "}
                and see wider disruption on our{" "}
                <Link className="faq-link" to="/travelnews">
                  Travel News
                </Link>{" "}
                page.
              </p>
            </div>
          </section>

          {/* BOOKING & VEHICLES */}
          <section className="faq-section" id="booking">
            <h2>🚘 Booking & Vehicles</h2>

            <div className="faq-item">
              <h3>How do I book a taxi?</h3>
              <p>
                Use our{" "}
                <Link className="faq-link" to="/">
                  Instant Fare Quote
                </Link>{" "}
                form on the homepage. Enter your pickup and drop-off locations,
                add passengers and bags, and you’ll see a{" "}
                <strong>fixed-price fare</strong>. You can then confirm and pay
                securely online.
              </p>
            </div>

            <div className="faq-item">
              <h3>What vehicle do I need?</h3>
              <p>Your vehicle is chosen based on passengers and luggage.</p>
              <ul>
                <li>🚕 1–4 passengers → Saloon / Estate</li>
                <li>
                  🚐 5–8 passengers →{" "}
                  <Link className="faq-link" to="/minibus-hire">
                    Minibus hire
                  </Link>
                </li>
                <li>🚌 9–14 passengers → Large Minibus</li>
                <li>
                  🚌 15–25 passengers →{" "}
                  <Link className="faq-link" to="/coach-hire">
                    Coach hire
                  </Link>
                </li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Can I travel from Heathrow to Slough, Windsor or London?</h3>
              <p>Yes, that’s our core service area. See our dedicated pages:</p>
              <ul>
                <li>
                  ✈️{" "}
                  <Link className="faq-link" to="/heathrow-taxi">
                    Heathrow taxi
                  </Link>
                </li>
                <li>
                  🚕{" "}
                  <Link className="faq-link" to="/slough-taxi">
                    Slough taxi
                  </Link>
                </li>
                <li>
                  🏰{" "}
                  <Link className="faq-link" to="/windsor-taxi">
                    Windsor taxi
                  </Link>
                </li>
                <li>
                  🌉{" "}
                  <Link className="faq-link" to="/maidenhead-taxi">
                    Maidenhead taxi
                  </Link>
                </li>
                <li>
                  🚌{" "}
                  <Link className="faq-link" to="/heathrow-minibus-hire">
                    Heathrow minibus hire
                  </Link>{" "}
                  and{" "}
                  <Link className="faq-link" to="/heathrow-coach-hire">
                    Heathrow coach hire
                  </Link>
                  .
                </li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Can I amend or cancel my booking?</h3>
              <p>
                Please contact us as soon as possible if you need to change your
                booking. Changes and cancellations are handled in line with the
                terms shown during checkout. For major changes (time, date,
                vehicle size), a new fare may apply.
              </p>
            </div>
          </section>

          {/* AIRPORT COLLECTIONS & PICKUPS */}
          <section className="faq-section" id="airports">
            <h2>🛬 Airport Collections & Pickups</h2>

            <div className="faq-item">
              <h3>Which airports do you cover?</h3>
              <p>
                We regularly serve{" "}
                <strong>Heathrow, Gatwick, Luton, Stansted</strong> and{" "}
                <strong>London City</strong>. For Heathrow-specific information,
                visit our{" "}
                <Link className="faq-link" to="/heathrow">
                  Heathrow guide
                </Link>{" "}
                or{" "}
                <Link className="faq-link" to="/heathrow-taxi">
                  Heathrow taxi page
                </Link>
                .
              </p>
            </div>

            <div className="faq-item">
              <h3>How do I find my driver at the airport?</h3>
              <p>
                Your confirmation email will include detailed pickup
                instructions. For <strong>Meet &amp; Greet</strong>, your driver
                will wait inside the arrivals hall with a name board. For
                standard pickups, we’ll advise the most convenient drop-off /
                pickup area at the terminal.
              </p>
              <p>
                If you are unsure which terminal you land at, use our{" "}
                <Link className="faq-link" to="/terminalFinder">
                  Terminal Finder
                </Link>{" "}
                and{" "}
                <Link className="faq-link" to="/airlineContacts">
                  Airline Contacts
                </Link>{" "}
                tools.
              </p>
            </div>

            <div className="faq-item">
              <h3>What happens if my flight is delayed?</h3>
              <p>
                We monitor flight status for airport pickups wherever possible.
                If your flight is delayed, your driver will normally adjust
                their arrival time. If you experience a long delay or missed
                connection, please message us as soon as you have Wi-Fi or
                mobile signal so we can update your booking.
              </p>
            </div>

            <div className="faq-item">
              <h3>Do you operate 24/7?</h3>
              <p>
                Yes – as long as the journey is{" "}
                <strong>pre-booked</strong>. Early-morning and late-night
                pickups are common for Heathrow and Gatwick flights.
              </p>
            </div>
          </section>

          {/* PAYMENTS & EMAILS */}
          <section className="faq-section" id="payments">
            <h2>💳 Payments & Emails</h2>

            <div className="faq-item">
              <h3>What payment methods are accepted?</h3>
              <p>
                We use <strong>Stripe secure payments</strong> for all card
                transactions. You can pay using major debit/credit cards, Apple
                Pay and Google Pay. No cash is required for most bookings.
              </p>
            </div>

            <div className="faq-item">
              <h3>Will I get confirmation and driver details by email?</h3>
              <p>You will typically receive:</p>
              <ul>
                <li>📧 Booking Confirmation – reference & journey summary</li>
                <li>📧 Driver / Dispatch update – when your driver is assigned</li>
                <li>📧 Payment Receipt – from our Stripe system</li>
                <li>📧 Optional review request – to share feedback</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>I haven’t received my email – what should I do?</h3>
              <p>
                First, please check your spam or junk folder. If it’s not there,
                contact us via the{" "}
                <Link className="faq-link" to="/contact">
                  contact page
                </Link>{" "}
                and we’ll resend it. Make sure your email address is spelled
                correctly when booking.
              </p>
            </div>
          </section>

          {/* DRIVERS & LICENSING */}
          <section className="faq-section" id="drivers">
            <h2>🚖 Drivers & Licensing</h2>

            <div className="faq-item">
              <h3>Are your drivers licensed and insured?</h3>
              <p>
                Yes. All CheapCabGrab drivers are{" "}
                <strong>council-licensed</strong>, insured for{" "}
                <em>hire and reward</em> and follow strict local authority
                rules. Many of our drivers are experienced{" "}
                <strong>airport specialists</strong>.
              </p>
              <p>
                For more detail on training and checks, see our{" "}
                <Link className="faq-link" to="/our-drivers">
                  Our Drivers
                </Link>{" "}
                page.
              </p>
            </div>

            <div className="faq-item">
              <h3>Do you carry out DBS and safeguarding checks?</h3>
              <p>
                Enhanced DBS checks, right-to-work checks and medical standards
                are part of the local council licensing process. Many of our
                drivers also complete{" "}
                <strong>safeguarding</strong> and{" "}
                <strong>disability awareness</strong> training.
              </p>
            </div>

            <div className="faq-item">
              <h3>Can I smoke or vape in the vehicle?</h3>
              <p>
                No. Smoking and vaping are strictly prohibited in all vehicles
                under UK law and local council licensing rules.
              </p>
            </div>
          </section>

          {/* CONTACT & SUPPORT */}
          <section className="faq-section" id="support">
            <h2>📞 Contact & Support</h2>

            <div className="faq-item">
              <h3>What if I need help with an existing booking?</h3>
              <p>
                The quickest way is to reply to your confirmation email so we
                can see your booking reference. You can also use the form on our{" "}
                <Link className="faq-link" to="/contact">
                  contact page
                </Link>
                .
              </p>
            </div>

            <div className="faq-item">
              <h3>Can I contact you for last-minute bookings?</h3>
              <p>
                Yes — subject to vehicle availability. For very short notice
                bookings (for example, when your pickup is in under two hours),
                please call or WhatsApp so we can confirm if a driver is free
                before you travel.
              </p>
            </div>

            <div className="faq-item">
              <h3>Where can I learn more about your services?</h3>
              <p>Useful pages on our site include:</p>
              <ul>
                <li>
                  ✈️{" "}
                  <Link className="faq-link" to="/heathrow-taxi">
                    Heathrow Taxi
                  </Link>
                </li>
                <li>
                  🚐{" "}
                  <Link className="faq-link" to="/minibus-hire">
                    Minibus Hire
                  </Link>
                </li>
                <li>
                  🚌{" "}
                  <Link className="faq-link" to="/coach-hire">
                    Coach Hire
                  </Link>
                </li>
                <li>
                  🚗{" "}
                  <Link className="faq-link" to="/fleet">
                    Our Fleet
                  </Link>
                </li>
                <li>
                  👨‍✈️{" "}
                  <Link className="faq-link" to="/our-drivers">
                    Our Drivers
                  </Link>
                </li>
                <li>
                  🏨{" "}
                  <Link className="faq-link" to="/heathrow-hotels">
                    Heathrow Hotels
                  </Link>
                </li>
                <li>
                  🏰{" "}
                  <Link className="faq-link" to="/heathrow-attractions">
                    Heathrow Attractions
                  </Link>
                </li>
                <li>
                  🌍{" "}
                  <Link className="faq-link" to="/heathrow">
                    Heathrow Info
                  </Link>
                </li>
              </ul>
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN – REVIEWS */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>⭐ Customer Reviews</h2>
            <ReviewsBar />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Faq;

